import apiClient from '@/api/client';
import { UPLOAD } from '@/api/endpoints';
import type { UploadSession } from '@/api/types/upload.types';

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export interface CreateSessionResponse {
  id: string;
  event_id: string;
  status: string;
  total_files: number;
  total_bytes: number;
  created_at: string;
}

/** Create a new upload session for an event. */
export async function createUploadSession(
  eventId: string,
  totalFiles: number,
  totalBytes: number,
): Promise<CreateSessionResponse> {
  const { data } = await apiClient.post<CreateSessionResponse>(UPLOAD.SESSION, {
    event_id: eventId,
    total_files: totalFiles,
    total_bytes: totalBytes,
  });
  return data;
}

/** Fetch an existing upload session. */
export async function getUploadSession(sessionId: string): Promise<UploadSession> {
  const { data } = await apiClient.get<UploadSession>(UPLOAD.SESSION_DETAIL(sessionId));
  return data;
}

// ---------------------------------------------------------------------------
// Per-file registration
// ---------------------------------------------------------------------------

export interface RegisterFilePayload {
  original_filename: string;
  content_type: string;
  total_size: number;
  total_chunks: number;
  client_checksum: string;
}

export interface RegisterFileResponse {
  id: string;
  session_id: string;
  original_filename: string;
  content_type: string;
  total_size: number;
  total_chunks: number;
  uploaded_chunks: number;
  status: string;
}

/** Register a single file within a session. Returns a server-assigned upload id. */
export async function registerFileUpload(
  sessionId: string,
  payload: RegisterFilePayload,
): Promise<RegisterFileResponse> {
  const { data } = await apiClient.post<RegisterFileResponse>(
    UPLOAD.REGISTER_FILE,
    { ...payload, session_id: sessionId },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Chunk upload via XMLHttpRequest (for progress tracking)
// ---------------------------------------------------------------------------

export interface ChunkUploadResult {
  chunk_index: number;
  bytes_received: number;
}

/**
 * Upload a single chunk using XMLHttpRequest so we can track byte-level
 * progress via the `upload.onprogress` event.
 *
 * Returns a promise that resolves when the chunk is accepted by the server.
 */
export function uploadChunk(
  uploadId: string,
  chunkIndex: number,
  chunk: Blob,
  onProgress?: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<ChunkUploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const url = `${apiClient.defaults.baseURL}${UPLOAD.CHUNK(uploadId, chunkIndex)}`;

    xhr.open('PUT', url);

    // Attach auth token from the latest store state.
    // We lazily import to avoid circular deps, but since this is runtime
    // we can just read localStorage directly for the token.
    const authRaw = localStorage.getItem('photo-improver-auth');
    if (authRaw) {
      try {
        const parsed = JSON.parse(authRaw);
        const token = parsed?.state?.accessToken;
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
      } catch {
        // ignore parse errors
      }
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as ChunkUploadResult);
        } catch {
          resolve({ chunk_index: chunkIndex, bytes_received: chunk.size });
        }
      } else {
        reject(new Error(`Chunk upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during chunk upload'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Chunk upload timed out'));
    };

    // Respect external abort signals (pause / cancel).
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Upload aborted', 'AbortError'));
      });
    }

    xhr.timeout = 120_000; // 2 minutes per chunk

    const formData = new FormData();

    formData.append('chunk', chunk);

    xhr.send(formData);
  });
}

// ---------------------------------------------------------------------------
// Completion / abort
// ---------------------------------------------------------------------------

export interface CompleteUploadResponse {
  id: string;
  status: string;
  photo_id?: string;
}

/** Notify the server that all chunks for a file have been uploaded. */
export async function completeUpload(uploadId: string): Promise<CompleteUploadResponse> {
  const { data } = await apiClient.post<CompleteUploadResponse>(UPLOAD.COMPLETE(uploadId));
  return data;
}

/** Abort an in-progress file upload. */
export async function abortUpload(uploadId: string): Promise<void> {
  await apiClient.post(UPLOAD.ABORT(uploadId));
}
